import { Component, inject, Input, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { BuildingView } from '@api-proxies';
import { EntityCardSection } from '@enums/EntityCardSection';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { EntityTypeCustomFieldService } from '@http/clients/entity-type-custom-field.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { customFieldPlacement, ICardConfig } from '@utils/custom-field-placement';
import { GenericCardComponent, ObjectExtensionsService, ValidatorType } from 'ngx-lib';

@Component({
    selector: 'app-buildings-single-overview',
    templateUrl: './buildings-single-overview.component.html',
    styleUrls: ['./buildings-single-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericCardComponent]
})
export class BuildingsSingleOverviewComponent implements OnChanges {
    @ViewChild('mainInfoCard')
    mainInfoCard?: GenericCardComponent;

    @ViewChild('locationCard')
    locationCard?: GenericCardComponent;

    @ViewChild('customInfoCard')
    customInfoCard?: GenericCardComponent;

    @Input()
    building?: BuildingView;

    @Input()
    isReadOnly: boolean;

    factory: AppEditorFactory;

    entityCardSection: typeof EntityCardSection;
    cardsConfig: ICardConfig;

    companyHasArchived = false;
    productTypeHasArchived = false;
    usersHasArchived = false;

    private readonly entityCustomFieldService = inject(EntityTypeCustomFieldService);
    private readonly objectExtensionService = inject(ObjectExtensionsService);

    constructor() {
        this.factory = new AppEditorFactory();
        this.isReadOnly = true;
        this.cardsConfig = [];
        this.entityCardSection = EntityCardSection;
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (
            this.building &&
            changes['building'] &&
            !this.objectExtensionService.isEqualTo(changes['building'].currentValue, changes['building'].previousValue)
        ) {
            this.cardsConfig = {
                [EntityCardSection.BuildingMainInfo]: [
                    {
                        label: 'Name',
                        componentId: EditorType.textInput,
                        fieldName: 'name',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly,
                        validations: [
                            { type: ValidatorType.required },
                            { type: ValidatorType.string, args: { maxLength: 100 } }
                        ],
                        additionalInfo: {
                            shouldTrimSpaces: true
                        }
                    },
                    {
                        label: 'Alternate Name',
                        componentId: EditorType.multiInput,
                        fieldName: 'alternateNames',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
                    },
                    {
                        label: 'Class',
                        componentId: EditorType.buildingClass,
                        fieldName: 'buildingBuildingClasses',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Website',
                        componentId: EditorType.link,
                        fieldName: 'website',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 500 } }]
                    },
                    {
                        label: 'Description',
                        componentId: EditorType.textarea,
                        fieldName: 'description',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 1000 } }]
                    }
                ],
                [EntityCardSection.BuildingAdditionalInfo]: [
                    {
                        label: 'Property Manager',
                        componentId: EditorType.company,
                        fieldName: 'propertyManagerCompanyId',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly,
                        additionalInfo: {
                            isActive: true,
                            allowToNavigate: true
                        }
                    },
                    {
                        label: 'Number Of Floors',
                        componentId: EditorType.numberInput,
                        fieldName: 'numberOfFloors',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.string, args: { maxLength: 200 } }]
                    },
                    {
                        label: 'Building (sq ft)',
                        componentId: EditorType.numberInput,
                        fieldName: 'size',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Typical Floor Size (sq ft)',
                        componentId: EditorType.numberInput,
                        fieldName: 'floorSize',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Product Types',
                        componentId: EditorType.entity,
                        fieldName: 'buildingProductTypes',
                        entityId: 'buildingProductType',
                        bindContext: this.building,
                        additionalInfo: {
                            entityType: EntityTypes.Building
                        },
                        isReadOnly: this.isReadOnly
                    },
                    {
                        label: 'Salesperson',
                        componentId: EditorType.entityUser,
                        fieldName: 'buildingSalespersons',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly
                    }
                ],
                [EntityCardSection.BuildingLocation]: [
                    {
                        componentId: EditorType.address,
                        fieldName: 'addressLines',
                        bindContext: this.building,
                        isReadOnly: this.isReadOnly,
                        validations: [{ type: ValidatorType.required }],
                        additionalInfo: {
                            staticImageMap: true
                        }
                    }
                ],
                [EntityCardSection.BuildingCustomInfo]: []
            };

            if (!this.building.customFieldValues?.length)
                this.building.customFieldValues = await this.entityCustomFieldService.getCustomFieldsByEntityType(
                    EntityTypes.Building
                );

            this.building.customFieldValues.forEach((customField) => {
                customFieldPlacement(
                    this.cardsConfig,
                    customField,
                    EntityCardSection.BuildingCustomInfo,
                    this.isReadOnly
                );
            });
            await this.checkArchivedItems(this.building);
        }
    }

    validate(): boolean {
        const mainInfoCardValid = this.mainInfoCard ? this.mainInfoCard.validate() : false;
        const locationCardValid = this.locationCard ? this.locationCard.validate() : false;

        const customInfoCard = this.customInfoCard ? this.customInfoCard.validate() : true; // if no custom fields: set as valid

        const allValid = mainInfoCardValid && locationCardValid && customInfoCard;

        return allValid;
    }

    private async checkArchivedItems(building: BuildingView): Promise<void> {
        this.companyHasArchived = building.propertyManagerCompanyIsActive === false;
        this.productTypeHasArchived = building.buildingProductTypes?.some((c) => c.isActive === false) ?? false;
        this.usersHasArchived = building.buildingSalespersons?.some((c) => c.isActive === false) ?? false;
    }
}
